/**
 * The slide deck the player walks (plan docs/plans/lms-plan.md 6.1, 7.2;
 * WP-40). Pure: content in, deck out.
 *
 * - Every section's slides, in order (6.1: "the slide show runs through every
 *   section's slides in order").
 * - First, for a retrain or acknowledge assignment, a "What changed" slide
 *   carrying the change summary (7.2: "shown first, as its own slide too").
 * - Last, a "Key takeaways" slide (6.1: key_takeaways are "the last slide and
 *   the last reading section"; authored decks do not carry one).
 *
 * The two added slides are the player's own: their keys are never sent in
 * slides_seen (the server refuses an id the version does not have, and
 * derives slides_done from the version's slides only, 18.15).
 *
 * A knowledge_check slide blocks: it must be answered before the learner
 * moves past it (7.2), so it counts as seen only once answered.
 */
import type { CourseContent, Slide } from '../../content/types';

export type SyntheticSlide = 'change_summary' | 'takeaways';

export interface DeckSlide {
  /** The slide id, or a key of the player's own for an added slide. */
  key: string;
  /** The authored slide; null for an added one. */
  slide: Slide | null;
  synthetic: SyntheticSlide | null;
  /** Index into DeckResult.sections. */
  sectionIndex: number;
  /** True for an authored slide: its id is saved in slides_seen. */
  persisted: boolean;
  /** knowledge_check: must be answered to advance (7.2). */
  blocking: boolean;
}

export interface DeckSection {
  /** The content section id; the added slides use their own key. */
  id: string;
  title: string;
  /** Index of its first slide in the deck. */
  start: number;
  count: number;
  synthetic: boolean;
}

export interface DeckResult {
  slides: DeckSlide[];
  sections: DeckSection[];
  /** The authored slide ids, in deck order (what slides_done is derived from). */
  slideIds: string[];
}

export const CHANGE_SUMMARY_KEY = '__lms_change_summary';
export const TAKEAWAYS_KEY = '__lms_key_takeaways';

export interface DeckOptions {
  /** The change summary to show first (a retrain or acknowledge assignment), else null. */
  changeSummary?: string | null;
}

export function buildDeck(content: CourseContent, options: DeckOptions = {}): DeckResult {
  const slides: DeckSlide[] = [];
  const sections: DeckSection[] = [];
  const slideIds: string[] = [];

  const summary = typeof options.changeSummary === 'string' ? options.changeSummary.trim() : '';
  if (summary) {
    sections.push({ id: CHANGE_SUMMARY_KEY, title: 'What changed', start: 0, count: 1, synthetic: true });
    slides.push({ key: CHANGE_SUMMARY_KEY, slide: null, synthetic: 'change_summary', sectionIndex: 0, persisted: false, blocking: false });
  }

  const contentSections = Array.isArray(content?.sections) ? content.sections : [];
  contentSections.forEach((section) => {
    const list = Array.isArray(section?.slides) ? section.slides : [];
    if (list.length === 0) return;
    const sectionIndex = sections.length;
    sections.push({ id: section.id, title: section.title, start: slides.length, count: list.length, synthetic: false });
    list.forEach((slide) => {
      slides.push({
        key: slide.id,
        slide,
        synthetic: null,
        sectionIndex,
        persisted: true,
        blocking: slide.layout === 'knowledge_check',
      });
      slideIds.push(slide.id);
    });
  });

  const takeaways = Array.isArray(content?.key_takeaways) ? content.key_takeaways : [];
  if (takeaways.length > 0) {
    sections.push({ id: TAKEAWAYS_KEY, title: 'Key takeaways', start: slides.length, count: 1, synthetic: true });
    slides.push({ key: TAKEAWAYS_KEY, slide: null, synthetic: 'takeaways', sectionIndex: sections.length - 1, persisted: false, blocking: false });
  }

  return { slides, sections, slideIds };
}

/**
 * May the learner open slide `index`? Only when every knowledge check before
 * it has been answered (7.2); `answered` says whether a blocking slide has.
 */
export function canReach(deck: DeckSlide[], index: number, answered: (key: string) => boolean): boolean {
  if (index < 0 || index >= deck.length) return false;
  for (let i = 0; i < index; i += 1) {
    if (deck[i].blocking && !answered(deck[i].key)) return false;
  }
  return true;
}

/** The furthest slide the learner may open (the first unanswered knowledge check, or the last slide). */
export function furthestReachable(deck: DeckSlide[], answered: (key: string) => boolean): number {
  for (let i = 0; i < deck.length; i += 1) {
    if (deck[i].blocking && !answered(deck[i].key)) return i;
  }
  return Math.max(0, deck.length - 1);
}
